import type { AppRouter } from "@repo/api";
import type { inferRouterOutputs } from "@trpc/server";

export type ServerOnboardingData =
  inferRouterOutputs<AppRouter>["onboarding"]["getData"];
export type ExpertProfileData =
  inferRouterOutputs<AppRouter>["expertProfile"]["get"];

// Phone verification is intentionally absent as its own step: by the time this
// banner shows, the expert has already verified their phone (it's mandatory and
// precedes the profile step in /join). The "linq" step below is NOT phone
// verification, it is the opt-in SMS-line migration (Twilio -> Linq) that lets
// us text the expert the moment a study matches; it is gated behind a flag and
// server-side eligibility, so it only appears for experts who can act on it.
export type ExpertOnboardingStepId =
  | "resume"
  | "profile"
  | "interview"
  | "id"
  | "linq";

export interface ExpertOnboardingStep {
  id: ExpertOnboardingStepId;
  shortLabel: string;
  ctaText: string;
  // A null href means the step opens a modal in place (the Linq QR popup)
  // instead of navigating to a route. Route-backed steps carry their href.
  href: string | null;
}

const RESUME_STEP: ExpertOnboardingStep = {
  id: "resume",
  shortLabel: "Resume",
  ctaText: "Add your resume",
  href: "/join",
};

const PROFILE_STEP: ExpertOnboardingStep = {
  id: "profile",
  shortLabel: "Profile",
  ctaText: "Finish your profile",
  href: "/join",
};

/**
 * Same step, different destination. The rate is asked for inside the /join
 * profile wizard, but an expert who confirmed their profile before that existed
 * only needs the one field: sending them back through the wizard would be four
 * screens of detour to reach it.
 */
const RATE_ONLY_PROFILE_STEP: ExpertOnboardingStep = {
  id: "profile",
  shortLabel: "Profile",
  ctaText: "Set your hourly rate",
  href: "/me/settings#hourly-rate",
};

/**
 * Gates every UI surface that points at the onboarding AI interview, so its
 * placement can be rolled out to a slice of experts and measured. The route
 * itself is deliberately not gated: it stays reachable by direct link.
 */
export const ONBOARDING_INTERVIEW_FLAG = "onboarding-ai-interview";

/**
 * Gates the pre-call readiness gate on the onboarding interview: screen share,
 * camera and microphone, and framing, cleared before the room is created.
 *
 * Off is byte identical to going straight from the welcome card into the room,
 * which is what shipped: no step is rendered, no device is touched before
 * LiveKit asks for one, and the call is still billed only once the welcome CTA
 * is pressed. Separate from `ONBOARDING_INTERVIEW_FLAG` because the interview
 * being offered at all and the interview asking for a screen first are
 * different rollouts with different risks.
 */
export const ONBOARDING_INTERVIEW_GATE_FLAG =
  "onboarding-interview-precall-gate";

// Sits ahead of ID so it becomes the CTA first: no study hard-requires identity
// verification, and the interview is what feeds matching. The Linq step sits
// there for the same reason.
const INTERVIEW_STEP: ExpertOnboardingStep = {
  id: "interview",
  shortLabel: "Interview",
  ctaText: "Help us know you better",
  href: "/join/interview",
};

const ID_STEP: ExpertOnboardingStep = {
  id: "id",
  shortLabel: "ID",
  ctaText: "Verify your identity",
  href: "/me/settings/identity?startVerification=1#identity",
};

const LINQ_STEP: ExpertOnboardingStep = {
  id: "linq",
  shortLabel: "Notifications",
  ctaText: "Enable notifications",
  href: null,
};

export interface ResolvedOnboardingStep extends ExpertOnboardingStep {
  complete: boolean;
}

export interface ResolveOnboardingStepsParams {
  onboarding: ServerOnboardingData;
  profile: ExpertProfileData | null | undefined;
  // Pass the ONBOARDING_INTERVIEW_FLAG result. Off means the step is absent
  // entirely, so an expert outside the rollout sees the pre-interview ordering.
  interviewEnabled: boolean;
  interviewComplete: boolean;
  // Linq is opt-in and lives behind a PostHog flag + server eligibility
  // (the expert must have an active phone). Pass `linqEligible` already
  // ANDed with the flag so the step only appears for experts who can act on it.
  linqEligible: boolean;
  linqComplete: boolean;
  // Client-only stand-down, read from localStorage by the banner. Needed because
  // `linqComplete` can only ever be flipped by the expert texting us from their
  // own phone, and some carrier routes cannot reach our lines at all: without
  // an exit, an expert who declines or cannot finish would hold the banner on
  // this step forever and never reach ID again. It lapses rather than sticking,
  // so the Twilio-only pool keeps draining instead of going quiet on one no.
  linqSnoozed?: boolean;
}

/**
 * Resolves the ordered onboarding steps together with their completion state in
 * a single pass. Steps and completion share one source of truth so they can
 * never drift apart (the stepper UI indexes steps by position).
 */
export function resolveOnboardingSteps(
  params: ResolveOnboardingStepsParams,
): ResolvedOnboardingStep[] {
  const {
    onboarding,
    profile,
    interviewEnabled,
    interviewComplete,
    linqEligible,
    linqComplete,
    linqSnoozed = false,
  } = params;

  // /join writes sourcesStepCompletedAt when the user finishes the sources
  // substep (either uploads a resume or skips). The legacy wizard wrote
  // resumeS3Key into User.privateMetadata.onboarding instead, so cover both.
  const legacyResumeKey =
    onboarding.user.privateMetadata?.onboarding?.resumeS3Key;
  const resumeDone =
    Boolean(profile?.sourcesStepCompletedAt) ||
    Boolean(profile?.resumeS3Key) ||
    Boolean(legacyResumeKey);
  // Only EDITED counts as done. ENRICHED means LinkedIn/resume auto-populated
  // the profile but the expert hasn't confirmed it in /join; exactly the state
  // a skipped user is in. Only EDITED flips onboardingFlowStatus=COMPLETED and
  // ungates applying, so counting ENRICHED here would mark "profile" done for a
  // user who still can't start an opportunity and nudge them at ID instead.
  const profileConfirmed = profile?.enrichmentStatus === "EDITED";
  const rateSet = onboarding.user.minimumHourlyRate != null;
  const profileDone = profileConfirmed && rateSet;
  const idVerified = onboarding.user.verificationStatus === "VERIFIED";

  // Order is the whole reach of a step: the banner only ever surfaces the first
  // incomplete one, so anything last is invisible to everyone still short of it.
  // Linq sits ahead of ID for the same reason Interview does - ID hard-gates no
  // study, while the Linq link is what lets us text an expert the moment one
  // matches. Sitting there is only safe because the step stands down on request;
  // see `linqSnoozed`.
  const steps: ResolvedOnboardingStep[] = [
    { ...RESUME_STEP, complete: resumeDone },
    {
      ...(profileConfirmed ? RATE_ONLY_PROFILE_STEP : PROFILE_STEP),
      complete: profileDone,
    },
    ...(interviewEnabled
      ? [{ ...INTERVIEW_STEP, complete: interviewComplete }]
      : []),
    ...(linqEligible && !linqSnoozed
      ? [{ ...LINQ_STEP, complete: linqComplete }]
      : []),
    { ...ID_STEP, complete: idVerified },
  ];

  return steps;
}

export function getNextIncompleteStep(
  steps: ResolvedOnboardingStep[],
): ResolvedOnboardingStep | null {
  return steps.find((s) => !s.complete) ?? null;
}
